import {Button, Grid, useTheme} from "@mui/material";
import React, {useContext, useEffect, useRef, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {CellClassParams, ColDef, EditableCallbackParams, ICellRendererParams, RowNode} from "ag-grid-community";
import {DEFAULT_MATRIX_COL_DEF, maxNumberYupLimit, SET_SNACKBAR} from "../../../constants";
import {useTranslation} from "react-i18next";
import {GridApi} from "ag-grid-enterprise";
import {OWRouteDetail} from "../../../api/types/owRouteDetail";
import {useNavigate} from "react-router-dom";
import CustomBanner from "../../../common/components/CustomBanner";
import {useAncillaryExceptionRepository} from "../../../api/repositories/AncillaryExceptionRepository";
import dayjs from "dayjs";
import {DialogInterface} from "../../../api/types/generic";
import DialogComponent from "../../../common/components/DialogComponent";
import {useAgGridLocale} from "../../../../../config/globalConfig";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import localizedFormat from "dayjs/plugin/localizedFormat";
import DateCustomEditorComponent from "../../../common/components/DateCustomEditorComponent";
import {AncillaryExceptionEntityResponse} from "../../../api/types/ancillaryException";
import {useOWRouteExceptionDetailRepository} from "../../../api/repositories/OWRouteExceptionDetailRepository";
import {OWRouteExceptionDetail} from "../../../api/types/owRouteExceptionDetail";
import NumberCustomEditorComponent from "../../../common/components/NumberCustomEditorComponent";
import BasicToolbar from "../../../common/components/BasicToolbar";
import {i18nConvertNumberWithDecimals} from "../../../utils";

dayjs.extend(localizedFormat);

const OWRouteExceptionMatrix = ({
                                    owAncillaryExceptionId,
                                    readOnly,
                                    owAncillaryExceptionInUse,
                                    setValue
                                }: {
    owAncillaryExceptionId: number | null
    readOnly: boolean
    owAncillaryExceptionInUse: boolean
    setValue: Function
}): JSX.Element => {
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Theme
    const theme = useTheme();

    // Use AgGrid Locale
    const agGridLocale = useAgGridLocale();

    // Use Ref
    const gridRef = useRef<AgGridReact>(null);

    // Use State
    const [gridApi, setGridApi] = useState<GridApi | null>(null);

    // Use Navigate
    const navigate = useNavigate();

    // Use State
    const [, setAncillaryException] = useState<AncillaryExceptionEntityResponse | null>(null);
    // const [ owLocationGroupDictionary, setOWLocationGroupDictionary ] = useState<{ [key: number]: string }>({});
    const [showOnlyEditable, setShowOnlyEditable] = useState<boolean>(false);
    const [owRouteExceptionDetails, setOWRouteExceptionDetails] = useState<OWRouteExceptionDetail[]>([]);
    const [originalOWRouteExceptionDetails, setOriginalOWRouteExceptionDetails] = useState<OWRouteExceptionDetail[]>([]);
    const [dialog, setDialog] = useState<DialogInterface | null>(null);

    // Use Context
    const {dispatchGlobalData} = useContext(GlobalContext);

    // Use Repository Hooks
    const {
        getOWRouteExceptionDetails,
        putOWRouteExceptionDetails
    } = useOWRouteExceptionDetailRepository();
    const {getAncillaryException} = useAncillaryExceptionRepository();
    const {getOWRouteDetailsFromCopyAncillary} = useOWRouteExceptionDetailRepository();

    const getMatrixInfo = async () => {
        if (owAncillaryExceptionId) {
            try {
                const ancillaryException: AncillaryExceptionEntityResponse = await getAncillaryException(owAncillaryExceptionId);
                setAncillaryException(ancillaryException);

                let owRouteExceptionDetails = (await getOWRouteExceptionDetails(owAncillaryExceptionId))
                    .map((owRouteExceptionDetail: OWRouteExceptionDetail) => {
                        return {
                            ...owRouteExceptionDetail,
                            amount: Number(owRouteExceptionDetail.amount) / 100
                        };
                    });

                //se ancillary exception details è vuoto, reinizializzare la matrice da accessorio
                if (owRouteExceptionDetails?.length === 0) {
                    owRouteExceptionDetails = (await getOWRouteDetailsFromCopyAncillary({
                        originalAncillaryId: ancillaryException.prcAncillary?.id as number,
                        destinationExceptionAncillaryId: owAncillaryExceptionId as number,
                        fixedDiscount: ancillaryException?.relatedDiscountIncreaseFixed as number,
                        percentDiscount: ancillaryException?.relatedDiscountIncreasePercentage as number
                    })).map(el => ({...el, amount: Number(el.amount) / 100}));
                }

                setOWRouteExceptionDetails(owRouteExceptionDetails);
                setOriginalOWRouteExceptionDetails(JSON.parse(JSON.stringify(owRouteExceptionDetails)));
            } catch (err) {
            }
        }
    };

    // Use Effect
    useEffect(() => {

        getMatrixInfo();
        // eslint-disable-next-line
    }, []);

    // ************ AG GRID START ************ //
    const onGridReady = (params: any) => {
        setGridApi(params.api);
        params.api.setDatasource(owRouteExceptionDetails);
    };

    const cellValueSetter = (params: any) => {
        console.log(params)
        if (params?.newValue !== null && params?.newValue !== undefined) {
            params.newValue = (params.newValue + '').replace(/,/g, '.');
        }
        if (!Number(params.newValue) && params.newValue != 0) {
            return false;
        } else if (params.newValue <= maxNumberYupLimit && params.newValue >= 0) {
            params.data[params.colDef.field.split(".")[0]] = params.newValue;
            return true;
        } else return false;
    };

    const columnDefs: ColDef<OWRouteExceptionDetail>[] = [
        ...[1].map((el) => ({
            headerName: t("pricing:owRouteDetailMatrix.labels.locationGroup"),
            headerTooltip: t("pricing:owRouteDetailMatrix.labels.locationGroup"),
            children: [
                {
                    headerName: t("pricing:owRouteDetailMatrix.labels.from"),
                    headerTooltip: t("pricing:owRouteDetailMatrix.labels.from"),
                    field: "locationFromId",
                    flex: 1,
                    editable: false,
                    pinned: "left",
                    sortable: false,
                    cellRenderer: (params: ICellRendererParams<OWRouteDetail>) => params.data?.locationFrom.name
                },
                {
                    headerName: t("pricing:owRouteDetailMatrix.labels.to"),
                    headerTooltip: t("pricing:owRouteDetailMatrix.labels.to"),
                    field: "locationToId",
                    flex: 1,
                    editable: false,
                    pinned: "left",
                    cellRenderer: (params: ICellRendererParams<OWRouteDetail>) => params.data?.locationTo.name
                }
            ]
        })),

        ...[1].map(() => ({
            headerName: t("pricing:owRouteDetailMatrix.labels.amount"),
            headerTooltip: t("pricing:owRouteDetailMatrix.labels.amount"),
            field: "amount",
            flex: 1,
            editable: (params: EditableCallbackParams) => {
                return dayjs().diff(dayjs(params.data.to), "minutes") < 0 && Boolean(params.data?.editable) && !readOnly;
            },
            cellEditor: "amountCustomEditor",
            valueSetter: cellValueSetter,
            cellRenderer: (params: ICellRendererParams) => i18nConvertNumberWithDecimals(params.data.amount),
            cellStyle: (params: CellClassParams) => ({
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                textAlign: "right",
                padding: 5,
                borderRight: "2px solid #E0E0E0",
                backgroundColor: dayjs().diff(dayjs(params.data.to), "minutes") < 0 ? "" : theme.palette.neutral.light
            })
        })),
        ...[1].map((el) => ({
            headerName: t("pricing:owRouteDetailMatrix.labels.date"),
            headerTooltip: t("pricing:owRouteDetailMatrix.labels.date"),
            children: [
                {
                    headerName: t("pricing:owRouteDetailMatrix.labels.from"),
                    headerTooltip: t("pricing:owRouteDetailMatrix.labels.from"),
                    field: "from",
                    flex: 1,
                    editable: (params: ICellRendererParams) => {
                        return dayjs().diff(dayjs(params.data.to), "minutes") < 0 && Boolean(params.data?.editable) && !readOnly;
                    },
                    cellEditor: "dateCustomEditor",
                    cellRenderer: (params: ICellRendererParams<OWRouteDetail>) => dayjs(params.data?.from).format("L LT")

                    // return
                    // <LocalizationProvider dateAdapter={ AdapterDayjs } adapterLocale={ i18n.language }>
                    // </LocalizationProvider>;
                    // }
                    ,
                    cellStyle: (params: CellClassParams) => ({
                        backgroundColor: dayjs().diff(dayjs(params.data.to), "minutes") < 0 ? "" : theme.palette.neutral.light
                    })
                },
                {
                    headerName: t("pricing:owRouteDetailMatrix.labels.to"),
                    headerTooltip: t("pricing:owRouteDetailMatrix.labels.to"),
                    field: "to",
                    flex: 1,
                    editable: (params: { data: { editable: any; }; }) => Boolean(params.data?.editable) && !readOnly,
                    cellEditor: "dateCustomEditor",
                    cellRenderer: (params: ICellRendererParams<OWRouteDetail>) => dayjs(params.data?.to).format("L LT")
                    // <LocalizationProvider
                    // dateAdapter={ AdapterDayjs } adapterLocale={ i18n.language }>

                    // </LocalizationProvider>
                }
            ]
        }))
    ];

    const defaultColDef = DEFAULT_MATRIX_COL_DEF;
    const frameworkComponents = {
        dateCustomEditor: DateCustomEditorComponent,
        amountCustomEditor: NumberCustomEditorComponent
    };
    // ************ AG GRID - END ************ //

    const handleClose = () => {
        setDialog(null);
        navigate("../ow/ow-ancillary-exception");
    };

    const getRowsFromAgGrid = (): OWRouteExceptionDetail[] => {
        const rowsFromAgGrid: OWRouteExceptionDetail[] = [];
        if (gridApi) gridApi.forEachNodeAfterFilterAndSort((node: RowNode<OWRouteExceptionDetail>) => rowsFromAgGrid.push({
            ...node.data as OWRouteExceptionDetail,
            amount: (node.data as OWRouteExceptionDetail).amount * 100
        } as OWRouteExceptionDetail));
        return rowsFromAgGrid;
    };

    const handlePersistMatrix = async () => {
        const owRouteExceptionDetails: OWRouteExceptionDetail[] = getRowsFromAgGrid();
        try {
            await putOWRouteExceptionDetails({owRouteExceptionDetails: owRouteExceptionDetails});
            setDialog(null);
            dispatchGlobalData(SET_SNACKBAR.SUCCESS);
            await getMatrixInfo();
            !owAncillaryExceptionId && navigate("../ow/ow-ancillary-exception");
        } catch (e) {
        }
    };

    // set background colour on even rows again, this looks bad, should be using CSS classes
    const getRowStyle = (params: any) => {
        if (!params.data.editable) {
            return {backgroundColor: theme.palette.neutral.light};
        }
    };

    const toolbar = {
        renderActions: () => (
            <BasicToolbar
                activeSwitch={{
                    label: t("pricing:rentalPeriods.form.labels.showActive"),
                    handleChange: (checked: boolean) => {
                        setShowOnlyEditable(checked);
                        setOWRouteExceptionDetails(checked ? originalOWRouteExceptionDetails.filter(r => dayjs(r.to) > dayjs()) : originalOWRouteExceptionDetails);
                    },
                    value: showOnlyEditable as boolean
                }}
            />
        )
    };

    return (
        <Grid container spacing={2} alignItems="start">
            {!readOnly
                && owRouteExceptionDetails?.length > 0
                && owRouteExceptionDetails[0].id !== null
                && owAncillaryExceptionInUse
                &&
                <CustomBanner message={t("pricing:owAncillaryExceptionMatrix.banner")} severity={"warning"}/>}
            {owRouteExceptionDetails?.length > 0
                && owRouteExceptionDetails[0].id === null
                &&
                <CustomBanner message={t("pricing:ancillaryExceptionMatrix.restoredMatrix")} severity={"warning"}/>
            }
            <Grid item xs={12}>
                <AgGridCustomTable
                    rowData={owRouteExceptionDetails}
                    rowSelection={"multiple"}
                    suppressRowClickSelection={true}
                    enableRangeSelection={true}
                    enableCellTextSelection={false}
                    gridRef={gridRef}
                    pagination={false}
                    defaultColDef={defaultColDef}
                    columnDefs={columnDefs}
                    localeText={agGridLocale}
                    tooltipShowDelay={0}
                    domLayout="autoHeight"
                    onGridReady={onGridReady}
                    frameworkComponents={frameworkComponents}
                    getRowStyle={getRowStyle}
                    toolbar={toolbar}
                />
                {!readOnly &&
                    <Grid container item xs={12} justifyContent="space-between" columnGap={2} my={2}>
                        <Grid>
                            <Button color={"accent"}
                                    type={"button"}
                                    onClick={() => setDialog({
                                        open: true,
                                        message: t("pricing:dialogs.messages.looseChanging"),
                                        onClickConfirm: () => {
                                            handleClose()
                                        }
                                    })}
                            >
                                {t("pricing:actions.close")}
                            </Button>
                        </Grid>
                        <Grid>
                            <Button variant="outlined"
                                    sx={{marginRight: "15px"}}
                                    color={"accent"}
                                    type={"button"}
                                    onClick={() => setDialog({
                                        open: true,
                                        message: t("pricing:dialogs.messages.looseChanging"),
                                        onClickConfirm: () => {
                                            window.close()
                                        }
                                    })}
                            >
                                {t("pricing:actions.back")}
                            </Button>
                            {owRouteExceptionDetails.length > 0 &&
                                <Button color={"accent"}
                                        type={"button"}
                                        onClick={() => setDialog({
                                            open: true,
                                            message:
                                                owAncillaryExceptionId
                                                && owRouteExceptionDetails?.length > 0
                                                && owRouteExceptionDetails[0].id !== null ?
                                                    t("pricing:owAncillaryExceptionMatrix.banner")
                                                    :
                                                    t("pricing:ancillaryMatrix.dialogContents.createAncillaryMatrix"),
                                            onClickConfirm: handlePersistMatrix
                                        })}
                                >
                                    {t("pricing:actions.confirm")}
                                </Button>
                            }
                        </Grid>
                    </Grid>
                }
            </Grid>
            {dialog?.open &&
                <DialogComponent
                    type={"info"}
                    onClickConfirm={dialog.onClickConfirm}
                    setOpen={() => setDialog(null)}
                    open={dialog.open}
                    message={dialog.message}
                />
            }
        </Grid>
    );
};

export default OWRouteExceptionMatrix;
